import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { listTemplates, templateFolders, TEMPLATE_FOLDERS, folderMeta } from "../../lib/storage";
import { insertHTML, restore, exec, getSavedRange } from "./cmd";
import { esc } from "../../lib/pages";
import { savedLists, saveList } from "../../lib/saved";
import { listHTML } from "./SavedFieldsDialog";

export const LinkDialog = ({ open, onClose }) => {
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [hasSel, setHasSel] = useState(false);
  useEffect(() => {
    if (!open) return;
    const r = getSavedRange();
    setHasSel(!!r && !r.collapsed);
    setText(r && !r.collapsed ? r.toString() : "");
    setUrl("https://");
  }, [open]);
  const submit = (e) => {
    e.preventDefault();
    const href = /^(https?:|mailto:|tel:)/i.test(url) ? url : `https://${url}`;
    onClose();
    setTimeout(() => {
      restore();
      if (hasSel && text === getSavedRange()?.toString()) exec("createLink", href);
      else insertHTML(`<a href="${esc(href)}" target="_blank" rel="noopener noreferrer">${esc(text || href)}</a>&nbsp;`);
    }, 0);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="link-dialog" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader><DialogTitle>Insert hyperlink</DialogTitle><DialogDescription>Ctrl+click a link in the document to open it.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1"><Label>Text to display</Label><Input value={text} onChange={(e) => setText(e.target.value)} data-testid="link-text-input" /></div>
          <div className="space-y-1"><Label>Address</Label><Input autoFocus value={url} onChange={(e) => setUrl(e.target.value)} data-testid="link-url-input" /></div>
          <DialogFooter><Button type="submit" disabled={url.length < 4} data-testid="link-insert-btn">Insert link</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const DropdownFieldDialog = ({ open, onClose }) => {
  const [opts, setOpts] = useState("Option 1\nOption 2\nOption 3");
  const [lists, setLists] = useState([]);
  const [save, setSave] = useState(false);
  const [name, setName] = useState("");
  useEffect(() => { if (open) { setLists(savedLists()); setSave(false); setName(""); } }, [open]);
  const submit = () => {
    const list = opts.split("\n").map((s) => s.trim()).filter(Boolean);
    if (!list.length) return;
    if (save && name.trim()) saveList(name.trim(), list);
    onClose();
    setTimeout(() => insertHTML(listHTML(list)), 0);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100%-2rem)] rounded-lg sm:max-w-sm" data-testid="dropdown-field-dialog" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader><DialogTitle>Drop-down list field</DialogTitle><DialogDescription>One choice per line. Readers pick a value right in the page; exports keep the chosen value.</DialogDescription></DialogHeader>
        {lists.length > 0 && (
          <select defaultValue="" onChange={(e) => { const l = lists.find((x) => x.name === e.target.value); if (l) { setOpts(l.options.join("\n")); setName(l.name); } }} className="h-9 rounded-md border bg-background px-2 text-sm" data-testid="dropdown-saved-select">
            <option value="" disabled>Use a saved list…</option>
            {lists.map((l) => <option key={l.name} value={l.name}>{l.name}</option>)}
          </select>
        )}
        <Textarea rows={6} value={opts} onChange={(e) => setOpts(e.target.value)} data-testid="dropdown-options-input" />
        <label className="flex items-center gap-2 text-sm"><Checkbox checked={save} onCheckedChange={(v) => setSave(!!v)} data-testid="dropdown-save-checkbox" />Save this list for reuse</label>
        {save && <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="List name, e.g. Invoice status" data-testid="dropdown-save-name-input" />}
        <DialogFooter><Button onClick={submit} disabled={save && !name.trim()} data-testid="dropdown-insert-btn">Insert field</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const SaveTemplateDialog = ({ open, onClose, defaultName, encrypted, onSave }) => {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [blankSigs, setBlankSigs] = useState(true);
  const [folder, setFolder] = useState("General");
  const [folders, setFolders] = useState(TEMPLATE_FOLDERS);
  useEffect(() => {
    if (!open) return;
    setName(defaultName || "My template"); setDesc(""); setBlankSigs(true); setFolder("General");
    listTemplates().then((l) => setFolders(templateFolders(l)));
  }, [open, defaultName]);
  const submit = async (e) => {
    e.preventDefault();
    await onSave(name.trim() || "My template", desc.trim(), blankSigs, folder.trim() || "General");
    onClose();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="save-template-dialog" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Save as template</DialogTitle>
          <DialogDescription>Saves the current content, page setup, header/footer and fields. Start new documents from it on the library page.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1"><Label>Template name</Label><Input autoFocus value={name} onChange={(e) => setName(e.target.value)} data-testid="template-name-input" /></div>
          <div className="space-y-1"><Label>Short description (optional)</Label><Input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="e.g. Monthly client report" data-testid="template-desc-input" /></div>
          <FolderField value={folder} onChange={setFolder} folders={folders} />
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={blankSigs} onCheckedChange={(v) => setBlankSigs(!!v)} data-testid="template-clear-signatures-checkbox" />Empty any filled signature and initials fields</label>
          {encrypted && <p className="text-xs text-amber-600" data-testid="template-encryption-warning">This document is password-protected, but templates are stored without encryption.</p>}
          <DialogFooter><Button type="submit" data-testid="template-save-btn">Save template</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const FolderField = ({ value, onChange, folders }) => (
  <div className="space-y-1.5">
    <Label>Folder</Label>
    <div className="flex flex-wrap gap-1.5">
      {folders.map((f) => (
        <button key={f} type="button" onClick={() => onChange(f)} data-testid={`template-folder-${f.toLowerCase().replace(/\s+/g, "-")}`}
          className={`h-7 px-2.5 rounded-full border text-xs transition-colors flex items-center gap-1.5 ${value === f ? "border-primary bg-primary/10 text-primary" : "hover:bg-accent"}`}>
          <span className="h-2 w-2 rounded-full" style={{ background: folderMeta(f).color }} />{f}</button>
      ))}
    </div>
    <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Or type a new folder name" className="h-8 text-sm" data-testid="template-folder-input" />
  </div>
);

export const EditTemplateDialog = ({ template, folders, onClose, onSave }) => {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [folder, setFolder] = useState("General");
  useEffect(() => { if (template) { setName(template.name); setDesc(template.desc || ""); setFolder(template.category || "General"); } }, [template]);
  return (
    <Dialog open={!!template} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="edit-template-dialog">
        <DialogHeader><DialogTitle>Edit template</DialogTitle><DialogDescription>Rename it or move it to another folder.</DialogDescription></DialogHeader>
        <form onSubmit={async (e) => { e.preventDefault(); await onSave({ name: name.trim() || template.name, desc: desc.trim(), category: folder.trim() || "General" }); onClose(); }} className="space-y-3">
          <div className="space-y-1"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-template-name" /></div>
          <div className="space-y-1"><Label>Description</Label><Input value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="edit-template-desc" /></div>
          <FolderField value={folder} onChange={setFolder} folders={folders} />
          <DialogFooter><Button type="submit" data-testid="edit-template-save-btn">Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
